import {
  collection,
  getDocs,
  query,
  limit,
  doc,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from './firebase';

const COL = 'quizScores';

export const QUIZ_MODES = ['survival', 'timeattack', 'guess'];
const DEFAULT_MODE = 'survival';

// I doc salvati prima dell'introduzione delle modalità non hanno `mode`:
// ricadono su 'survival' (l'unica modalità che esisteva), così i vecchi
// record/leaderboard restano validi senza bisogno di una migrazione.
function normalizeMode(mode) {
  return QUIZ_MODES.includes(mode) ? mode : DEFAULT_MODE;
}

const norm = (s) => String(s || '').trim().toLowerCase();

// Gruppi di email che appartengono alla stessa persona (chi accede all'app
// con più account Google). Il nome visualizzato NON viene usato per unire i
// punteggi: nella lega esistono persone diverse con lo stesso nome.
// Tenere allineato con la funzione sameQuizOwner() in firestore.rules.
const EMAIL_ALIASES = [
  ['marcop.paolini@gmail.com', 'marcoplavoro19@gmail.com'],
];

// Due punteggi appartengono alla stessa persona se coincide l'uid, l'email
// oppure se le email fanno parte dello stesso gruppo di alias.
export function isSamePerson(a, b) {
  if (a.userId && b.userId && a.userId === b.userId) return true;
  const ea = norm(a.email);
  const eb = norm(b.email);
  if (ea && ea === eb) return true;
  const group = EMAIL_ALIASES.find(g => g.includes(ea));
  if (group && group.includes(eb)) return true;
  return false;
}

// Un solo documento per persona PER MODALITÀ: l'ID del doc è
// `${userId}__${mode}` (con lo userId dell'account con cui è stato fatto il
// record). Salva SOLO se il punteggio supera il miglior punteggio già
// presente su Firestore per questa persona IN QUELLA MODALITÀ (su qualsiasi
// suo account); in quel caso i vecchi doc della stessa persona nella stessa
// modalità vengono eliminati. Non tocca mai i record delle altre modalità.
// Ritorna { saved, bestScore } così la UI può dire all'utente cosa è successo.
export async function saveQuizScore({
  userId, displayName, email, mode,
  score, correctCount, questionsAnswered, durationSecs = null, breakdown,
}) {
  const normalizedMode = normalizeMode(mode);
  const data = {
    userId,
    displayName: displayName || email || 'Anonimo',
    email: email || '',
    mode: normalizedMode,
    score,
    correctCount,
    questionsAnswered,
    durationSecs,
    breakdown,
    playedAt: Date.now(),
  };

  // Legge tutti i punteggi salvati: serve per trovare anche quelli della
  // stessa persona salvati con un altro account o senza campo userId
  const snap = await getDocs(query(collection(db, COL), limit(500)));
  const mine = snap.docs.filter(d => {
    const docData = d.data();
    return normalizeMode(docData.mode) === normalizedMode && isSamePerson(docData, data);
  });

  const bestScore = mine.reduce((max, d) => Math.max(max, Number(d.data().score) || 0), 0);

  if (Number(score) <= bestScore) {
    // Non è un record per questa modalità: non si salva nulla
    return { saved: false, bestScore };
  }

  const docId = `${userId}__${normalizedMode}`;

  // Nuovo record personale per questa modalità → scrivi sul doc canonico
  await setDoc(doc(db, COL, docId), data);

  // Elimina i vecchi punteggi (più bassi) della stessa persona NELLA STESSA
  // MODALITÀ, inclusi quelli di altri suoi account (best effort: se le
  // regole lo negano non blocca il salvataggio)
  await Promise.all(
    mine.filter(d => d.id !== docId).map(d => deleteDoc(d.ref).catch(() => {}))
  );

  return { saved: true, bestScore };
}

// Nessun orderBy → nessun indice necessario; ordinamento in JS
// Deduplica per persona (uid/email/nome) tenendo il punteggio migliore,
// filtrata sulla modalità richiesta.
export async function getLeaderboard(topN = 10, mode = DEFAULT_MODE) {
  const normalizedMode = normalizeMode(mode);
  const snap = await getDocs(query(collection(db, COL), limit(500)));
  const best = [];
  snap.docs.forEach(d => {
    const s = { id: d.id, ...d.data() };
    if (normalizeMode(s.mode) !== normalizedMode) return;
    const i = best.findIndex(x => isSamePerson(x, s));
    if (i === -1) best.push(s);
    else if ((Number(s.score) || 0) > (Number(best[i].score) || 0)) best[i] = s;
  });
  return best.sort((a, b) => b.score - a.score).slice(0, topN);
}

// Trova tutti i punteggi della persona (su qualsiasi suo account) in una
// modalità specifica. `user` è l'oggetto Firebase Auth (uid, email, displayName).
async function getPersonScores(user, mode) {
  if (!user?.uid) return [];
  const normalizedMode = normalizeMode(mode);
  const me = { userId: user.uid, email: user.email || '', displayName: user.displayName || '' };
  const snap = await getDocs(query(collection(db, COL), limit(500)));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(s => normalizeMode(s.mode) === normalizedMode && isSamePerson(s, me));
}

export async function getUserBestScore(user, mode = DEFAULT_MODE) {
  const scores = await getPersonScores(user, mode);
  if (!scores.length) return null;
  return scores.sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0))[0];
}
